import React, { useState, useEffect, useCallback } from 'react'
import useSWR from 'swr'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  TextField,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import CodeIcon from '@mui/icons-material/Code'
import { FormattedMessage, useIntl } from 'react-intl'
import Loading from '../../../../components/Loading'
import axiosInstance from '../../../../utils/axiosInstance'
import { Customer } from '../../../../types/customer'
import CvFollowUpSettings, {
  CvFollowUpValues,
  DEFAULT_FOLLOW_UP_DELAY_DAYS,
} from './CvFollowUpSettings'

interface EmailSettingsProps {
  customerId: string
  isEditMode: boolean
  onEmailSettingsChange?: (settings: EmailSettingData) => void
  customerType?: string
}

interface EmailSettingData {
  id?: string
  createdAt?: Date | null
  updatedAt?: Date | null
  // autoMailToIncomingLeads: boolean
  showDetailOnNewLeadEmail?: boolean
  emailTemplateBase: string
  inProgressText?: string
  rejectionText?: string
  successText?: string
  askForCVText?: string
  askForCvType?: 'upload' | 'custom'
  askForCvCustomText?: string
  askForCvCustomTitle?: string
  autoAskForCvOnIncomingLeads?: boolean
  autoFollowUpEnabled?: boolean
  followUpDelayDays?: number
  followUpCustomTitle?: string
  followUpCustomText?: string
}

const defaultFollowUpSettings = {
  autoFollowUpEnabled: false,
  followUpDelayDays: DEFAULT_FOLLOW_UP_DELAY_DAYS,
  followUpCustomTitle: '',
  followUpCustomText: '',
}

interface EmailSettingsResponse {
  data: {
    data: {
      emailSetting: EmailSettingData | null
    }
  }
}

const EmailSettings: React.FC<EmailSettingsProps> = ({
  customerId,
  isEditMode,
  onEmailSettingsChange,
  customerType,
}) => {
  const intl = useIntl()
  const [emailSettings, setEmailSettings] = useState<EmailSettingData>({
    // autoMailToIncomingLeads: false,
    showDetailOnNewLeadEmail: false,
    emailTemplateBase: '',
    rejectionText: '',
    askForCvType: 'custom',
    askForCvCustomText: '',
    askForCvCustomTitle: '',
    autoAskForCvOnIncomingLeads: false,
    ...defaultFollowUpSettings,
  })
  const [previewOpen, setPreviewOpen] = useState(false)
  const [hasBodyPlaceholder, setHasBodyPlaceholder] = useState(true)
  const [previewTab, setPreviewTab] = useState(0)
  const [templateExpanded, setTemplateExpanded] = useState(false)
  const [rejectionExpanded, setRejectionExpanded] = useState(false)
  const [askForCvExpanded, setAskForCvExpanded] = useState(false)
  const [useDefaultRecrusTemplate, setUseDefaultRecrusTemplate] = useState(true)
  const [cvPreviewModalOpen, setCvPreviewModalOpen] = useState(false)
  const [useDefaultCvTitle, setUseDefaultCvTitle] = useState(true)

  // Fetch email settings for the customer
  const {
    data: emailSettingsData,
    isLoading: isLoadingData,
    error,
  } = useSWR<EmailSettingsResponse>(
    customerId ? `/customer-email-settings/${customerId}` : null,
    axiosInstance
  )

  // Fetch customer data for email substitution
  const { data: customerData, isLoading: isLoadingCustomer } = useSWR<{
    data: { customer: Customer }
  }>(customerId ? `/customer/${customerId}` : null, axiosInstance)

  // Helper function to get customer email (defaultEmail or first email from emails array)
  const getCustomerEmail = useCallback((): string | undefined => {
    const customer = customerData?.data?.customer
    if (!customer) return undefined

    // Use defaultEmail if available and not empty
    if (customer.defaultEmail && customer.defaultEmail.trim() !== '') {
      return customer.defaultEmail
    }

    // Fall back to first email from emails array
    try {
      // Handle both string (JSON) and array formats
      const emails = Array.isArray(customer.emails)
        ? customer.emails
        : JSON.parse((customer.emails as string) || '[]')

      if (Array.isArray(emails) && emails.length > 0) {
        return emails[0] as string
      }
      return undefined
    } catch {
      return undefined
    }
  }, [customerData])

  useEffect(() => {
    // Only process data if we have a response and it's not still loading
    if (!isLoadingData && emailSettingsData && !isLoadingCustomer) {
      if (emailSettingsData.data?.data?.emailSetting) {
        const settings = emailSettingsData.data.data.emailSetting
        const cvType = settings.askForCvType || 'custom'
        setEmailSettings({
          id: settings.id,
          // autoMailToIncomingLeads: settings.autoMailToIncomingLeads || false,
          showDetailOnNewLeadEmail: settings.showDetailOnNewLeadEmail || false,
          emailTemplateBase: settings.emailTemplateBase || '',
          rejectionText: settings.rejectionText || '',
          askForCvType: cvType,
          askForCvCustomText: settings.askForCvCustomText || '',
          askForCvCustomTitle: settings.askForCvCustomTitle || '',
          autoAskForCvOnIncomingLeads: settings.autoAskForCvOnIncomingLeads || false,
          autoFollowUpEnabled: settings.autoFollowUpEnabled || false,
          followUpDelayDays: settings.followUpDelayDays ?? DEFAULT_FOLLOW_UP_DELAY_DAYS,
          followUpCustomTitle: settings.followUpCustomTitle || '',
          followUpCustomText: settings.followUpCustomText || '',
        })
        // Set default template checkbox based on whether both base template and CV text are empty
        setUseDefaultRecrusTemplate(
          (!settings.emailTemplateBase || settings.emailTemplateBase === '') &&
            (!settings.askForCvCustomText || settings.askForCvCustomText === '')
        )
        // Set default CV title checkbox based on whether custom title is empty
        setUseDefaultCvTitle(!settings.askForCvCustomTitle || settings.askForCvCustomTitle === '')
        setHasBodyPlaceholder(
          !settings.emailTemplateBase || settings.emailTemplateBase.includes('{{ body }}')
        )
      } else {
        const cvType = 'custom'
        setEmailSettings({
          // autoMailToIncomingLeads: false,
          showDetailOnNewLeadEmail: false,
          emailTemplateBase: '',
          rejectionText: '',
          askForCvType: cvType,
          askForCvCustomText: '',
          askForCvCustomTitle: '',
          autoAskForCvOnIncomingLeads: false,
          ...defaultFollowUpSettings,
        })
        setUseDefaultRecrusTemplate(true)
        setUseDefaultCvTitle(true)
        setHasBodyPlaceholder(true)
      }
    }
  }, [emailSettingsData, isLoadingData, customerData, isLoadingCustomer, error, getCustomerEmail])

  const handleTemplateChange = (value: string) => {
    const newSettings = { ...emailSettings, emailTemplateBase: value }
    setEmailSettings(newSettings)
    setHasBodyPlaceholder(value.includes('{{ body }}'))
    onEmailSettingsChange?.(newSettings)

    // Update default template checkbox based on content
    const hasContent =
      value.trim() !== '' ||
      (emailSettings.askForCvCustomText && emailSettings.askForCvCustomText.trim() !== '')
    if (hasContent && useDefaultRecrusTemplate) {
      setUseDefaultRecrusTemplate(false)
    } else if (!hasContent && !useDefaultRecrusTemplate) {
      setUseDefaultRecrusTemplate(true)
    }
  }

  const handleRejectionTextChange = (value: string) => {
    const newSettings = { ...emailSettings, rejectionText: value }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)
  }

  // const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const newSettings = {
  //     ...emailSettings,
  //     autoMailToIncomingLeads: event.target.checked,
  //   }
  //   setEmailSettings(newSettings)
  //   onEmailSettingsChange?.(newSettings)
  // }

  const handleAutoAskForCvCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSettings = {
      ...emailSettings,
      autoAskForCvOnIncomingLeads: event.target.checked,
    }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)
  }

  const handleFollowUpChange = (values: CvFollowUpValues) => {
    const newSettings = { ...emailSettings, ...values }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)
  }

  const handleShowDetailOnNewLeadEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSettings = {
      ...emailSettings,
      showDetailOnNewLeadEmail: event.target.checked,
    }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)
  }

  const handleUseDefaultTemplateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setUseDefaultRecrusTemplate(checked)

    if (checked) {
      // Clear both template and CV text when using default
      const newSettings = {
        ...emailSettings,
        emailTemplateBase: '',
        askForCvCustomText: '',
      }
      setEmailSettings(newSettings)
      onEmailSettingsChange?.(newSettings)
      setHasBodyPlaceholder(true)
    }
  }

  const handleCvTypeChange = (value: 'upload' | 'custom') => {
    const newSettings = {
      ...emailSettings,
      askForCvType: value,
      askForCvCustomText: useDefaultRecrusTemplate ? '' : emailSettings.askForCvCustomText,
    }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)
  }

  const handleCvCustomTextChange = (value: string) => {
    const newSettings = { ...emailSettings, askForCvCustomText: value }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)

    // Update default template checkbox based on content
    const hasContent =
      value.trim() !== '' ||
      (emailSettings.emailTemplateBase && emailSettings.emailTemplateBase.trim() !== '')
    if (hasContent && useDefaultRecrusTemplate) {
      setUseDefaultRecrusTemplate(false)
    } else if (!hasContent && !useDefaultRecrusTemplate) {
      setUseDefaultRecrusTemplate(true)
    }
  }

  const handleCvCustomTitleChange = (value: string) => {
    const newSettings = { ...emailSettings, askForCvCustomTitle: value }
    setEmailSettings(newSettings)
    onEmailSettingsChange?.(newSettings)

    // Update default CV title checkbox based on content
    if (value.trim() !== '' && useDefaultCvTitle) {
      setUseDefaultCvTitle(false)
    } else if (value.trim() === '' && !useDefaultCvTitle) {
      setUseDefaultCvTitle(true)
    }
  }

  const handleUseDefaultCvTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setUseDefaultCvTitle(checked)

    if (checked) {
      // Clear custom title when using default
      const newSettings = {
        ...emailSettings,
        askForCvCustomTitle: '',
      }
      setEmailSettings(newSettings)
      onEmailSettingsChange?.(newSettings)
    }
  }

  const getPreviewContent = (useRejectionText = false, useAskForCvText = false) => {
    if (!emailSettings.emailTemplateBase) return ''
    let bodyContent = ''

    if (useRejectionText) {
      bodyContent = emailSettings.rejectionText || '<p>No rejection text provided</p>'
    } else if (useAskForCvText) {
      const cvText = emailSettings.askForCvCustomText || '<p>No CV text provided</p>'
      if (emailSettings.askForCvType === 'upload') {
        bodyContent = `
          <div style="line-height: 1.6;">
            ${cvText}
            <br/><br/>
            <button
              disabled
              style="
                padding: 10px 20px;
                background: #1976d2;
                color: white;
                border: none;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 500;
                cursor: not-allowed;
                opacity: 0.8;
              "
            >
              📎 CV hochladen
            </button>
          </div>
        `
      } else {
        bodyContent = cvText
      }
    } else {
      bodyContent = '<p>Your Email body comes here!</p>'
    }

    return emailSettings.emailTemplateBase.replace('{{ body }}', bodyContent)
  }

  const getCvPreviewContent = () => {
    const customerName = customerData?.data?.customer?.name || 'Kundenname'
    const recLogo = `${window.location.origin}/rec-logo.png`

    const hasCustomText =
      !useDefaultRecrusTemplate &&
      emailSettings.askForCvCustomText &&
      emailSettings.askForCvCustomText.trim()
    const cvType = emailSettings.askForCvType || 'upload'
    const isCustomMode = !!hasCustomText && cvType === 'custom'

    const customBody = hasCustomText
      ? emailSettings.askForCvCustomText
      : 'Damit wir deine Bewerbung vollständig prüfen können, lade bitte kurz deinen Lebenslauf' +
        ' hoch. Wir nutzen dafür unser sicheres Bewerber-Portal von recruspin.' +
        ' <span style="color:#220D77;font-weight:bold;">Dauert weniger als 1 Minute.</span>'

    const sharedStyles = `
      body {
        font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
          Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        margin: 0;
        padding: 0;
        background-color: #f4f4f4;
      }
      .mainContainer { max-width: 600px; margin: 0 auto; padding: 20px; }
      .messageContainer {
        background-color: #ffffff;
        padding: 40px 30px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      .bodyText { font-size: 14px; color: #434243; line-height: 1.6; margin: 20px 0; }
      .footer {
        text-align: center;
        margin-top: 30px;
        padding-top: 20px;
        border-top: 1px solid #e0e0e0;
      }
      .recruspin-logo { width: 80px; margin: 0 auto 10px; }
      .footer-text { font-size: 12px; color: #777777; line-height: 1.5; }`

    const footer = `
        <div class="footer">
          <div class="recruspin-logo">
            <img src="${recLogo}" alt="recruspin logo" width="27" />
          </div>
          <div class="footer-text">
            recruspin unterstützt uns als technischer Dienstleister beim sicheren Datenupload.<br/>
            Deine Bewerbung bleibt selbstverständlich direkt bei ${customerName}.
          </div>
        </div>`

    if (isCustomMode) {
      return `<!DOCTYPE html>
<html lang="de">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>${sharedStyles}
    </style>
    <title>CV Upload Request</title>
  </head>
  <body>
    <div class="mainContainer">
      <div class="messageContainer">
        <div class="bodyText">${customBody}</div>
        ${footer}
      </div>
    </div>
  </body>
</html>`
    }

    const showUploadButton = !hasCustomText || cvType === 'upload'
    const uploadButtonHtml = showUploadButton
      ? '<a href="#" style="display:inline-block;padding:15px 40px;background-color:#B5F44A;' +
        'color:#3C541D;text-decoration:none;border-radius:300px;font-weight:bold;font-size:16px;' +
        'margin:20px 0;border:none;">Jetzt Lebenslauf hochladen</a>'
      : ''

    return `<!DOCTYPE html>
<html lang="de">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>${sharedStyles}
      .greeting { font-size: 16px; color: #333333; margin-bottom: 20px; }
      .title { font-size: 24px; font-weight: bold; color: #220D77; margin-bottom: 15px; }
      .jobInfo { font-size: 16px; color: #220D77; margin-bottom: 25px; }
      .signature { font-size: 14px; color: #333333; margin-top: 30px; }
    </style>
    <title>CV Upload Request</title>
  </head>
  <body>
    <div class="mainContainer">
      <div class="messageContainer">
        <div class="greeting">Hallo Max Mustermann,</div>
        <div class="title">vielen Dank für Deine Bewerbung!</div>
        <div class="jobInfo">Senior Developer ${customerName}.</div>
        ${uploadButtonHtml}
        <div class="bodyText">${customBody}</div>
        <div class="signature">
          <strong>Beste Grüße</strong><br/>
          ${customerName}<br/>
          (in Zusammenarbeit mit recruspin)
        </div>
        ${footer}
      </div>
    </div>
  </body>
</html>`
  }

  if (!customerId) {
    return <div>Customer ID is required</div>
  }

  if (isLoadingData) {
    return <Loading />
  }

  // Check if customer is a reseller (hide CV-related fields for resellers)
  const isReseller = customerType === 'partner'

  return (
    <div className="customerDetails subFormContainer">
      <div className="header">
        <FormattedMessage id="email-settings" />
      </div>
      <Box sx={{ p: 2 }}>
        {/* Auto Mail to Incoming Leads Checkbox (temporarily hidden) */}
        {/**
        <FormControlLabel
          control={
            <Checkbox
              checked={emailSettings.autoMailToIncomingLeads}
              onChange={handleCheckboxChange}
              disabled={!isEditMode}
            />
          }
          label={intl.formatMessage({ id: 'auto-mail-to-incoming-leads' })}
          sx={{ mb: 2 }}
        />
        */}

        {/* Show Lead Details in New Lead Email Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={emailSettings.showDetailOnNewLeadEmail || false}
              onChange={handleShowDetailOnNewLeadEmailChange}
              disabled={!isEditMode}
            />
          }
          label={intl.formatMessage({ id: 'show-detail-on-new-lead-email' })}
          sx={{ mb: 2 }}
        />

        {/* Auto Ask for CV on Incoming Leads Checkbox - Hidden for Resellers */}
        {!isReseller && (
          <FormControlLabel
            control={
              <Checkbox
                checked={emailSettings.autoAskForCvOnIncomingLeads || false}
                onChange={handleAutoAskForCvCheckboxChange}
                disabled={!isEditMode}
              />
            }
            label={intl.formatMessage({ id: 'auto-ask-for-cv-on-incoming-leads' })}
            sx={{ mb: 2 }}
          />
        )}

        {/* Use Default Recruspin Template Checkbox - Hidden for Resellers */}
        {!isReseller && (
          <FormControlLabel
            control={
              <Checkbox
                checked={useDefaultRecrusTemplate}
                onChange={handleUseDefaultTemplateChange}
                disabled={!isEditMode}
              />
            }
            label="Use default Recruspin Template"
            sx={{ mb: 3 }}
          />
        )}

        {/* Email Template Base - Expandable - Hidden for Resellers */}
        {!isReseller && (
          <Accordion
            expanded={templateExpanded}
            onChange={(_, isExpanded) => setTemplateExpanded(isExpanded)}
            sx={{
              mb: 2,
              bgcolor: 'grey.100',
              '&.MuiAccordion-root': {
                border: '1px solid',
                borderColor: 'grey.300',
              },
              '& .MuiAccordionSummary-root': {
                bgcolor: 'grey.200',
                '&:hover': {
                  bgcolor: 'grey.300',
                },
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FormattedMessage id="email-template-base" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {!hasBodyPlaceholder && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>
                    <FormattedMessage id="missing-body-placeholder" />
                  </AlertTitle>
                  <FormattedMessage id="body-placeholder-required" />
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  <FormattedMessage id="template-instructions" />
                </AlertTitle>
                <FormattedMessage id="body-placeholder-info" />
              </Alert>

              <TextField
                fullWidth
                multiline
                minRows={10}
                value={emailSettings.emailTemplateBase}
                onChange={(e) => handleTemplateChange(e.target.value)}
                placeholder={intl.formatMessage({ id: 'email-template-placeholder' })}
                disabled={!isEditMode || useDefaultRecrusTemplate}
                error={!hasBodyPlaceholder && emailSettings.emailTemplateBase.length > 0}
                helperText={
                  !hasBodyPlaceholder && emailSettings.emailTemplateBase.length > 0
                    ? intl.formatMessage({ id: 'body-placeholder-missing' })
                    : ''
                }
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Rejection Text - Expandable - Hidden for Resellers */}
        {!isReseller && (
          <Accordion
            expanded={rejectionExpanded}
            onChange={(_, isExpanded) => setRejectionExpanded(isExpanded)}
            sx={{
              mb: 2,
              bgcolor: 'grey.100',
              '&.MuiAccordion-root': {
                border: '1px solid',
                borderColor: 'grey.300',
              },
              '& .MuiAccordionSummary-root': {
                bgcolor: 'grey.200',
                '&:hover': {
                  bgcolor: 'grey.300',
                },
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FormattedMessage id="rejection-text" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  <FormattedMessage id="rejection-text-usage" />
                </AlertTitle>
                <FormattedMessage id="rejection-text-info" />
              </Alert>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1.5,
                  p: 1.5,
                  border: '1px dashed',
                  borderColor: 'primary.light',
                  borderRadius: 1,
                  bgcolor: 'primary.lighter',
                }}
              >
                <CodeIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
                  Available variables:
                </Typography>
                <Chip size="small" label="{{name}}" color="primary" variant="outlined" />
                <Chip size="small" label="{{jobTitle}}" color="primary" variant="outlined" />
                <Chip size="small" label="{{companyName}}" color="primary" variant="outlined" />
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  Paste these into your text — they will be replaced with the candidate&apos;s name,
                  job title, and company name.
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={6}
                value={emailSettings.rejectionText}
                onChange={(e) => handleRejectionTextChange(e.target.value)}
                placeholder={intl.formatMessage({ id: 'rejection-text-placeholder' })}
                disabled={!isEditMode || useDefaultRecrusTemplate}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Ask for CV - Expandable - Hidden for Resellers */}
        {!isReseller && (
          <Accordion
            expanded={askForCvExpanded}
            onChange={(_, isExpanded) => setAskForCvExpanded(isExpanded)}
            sx={{
              mb: 2,
              bgcolor: 'grey.100',
              '&.MuiAccordion-root': {
                border: '1px solid',
                borderColor: 'grey.300',
              },
              '& .MuiAccordionSummary-root': {
                bgcolor: 'grey.200',
                '&:hover': {
                  bgcolor: 'grey.300',
                },
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FormattedMessage id="ask-for-cv-text" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  <FormattedMessage id="ask-for-cv-usage" />
                </AlertTitle>
                <FormattedMessage id="ask-for-cv-info" />
              </Alert>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="cv-type-label">
                  <FormattedMessage id="cv-request-type" />
                </InputLabel>
                <Select
                  labelId="cv-type-label"
                  value={emailSettings.askForCvType || 'custom'}
                  label={intl.formatMessage({ id: 'cv-request-type' })}
                  onChange={(e) => handleCvTypeChange(e.target.value as 'upload' | 'custom')}
                  disabled={!isEditMode || useDefaultRecrusTemplate}
                >
                  <MenuItem value="upload">
                    <FormattedMessage id="cv-upload-option" />
                  </MenuItem>
                  <MenuItem value="custom">
                    <FormattedMessage id="cv-custom-option" />
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Custom CV Request Title */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useDefaultCvTitle}
                    onChange={handleUseDefaultCvTitleChange}
                    disabled={!isEditMode || useDefaultRecrusTemplate}
                  />
                }
                label="Use default email subject"
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth
                value={emailSettings.askForCvCustomTitle || ''}
                onChange={(e) => handleCvCustomTitleChange(e.target.value)}
                placeholder="Ihre Bewerbung für [Kunde]: [Stellenbezeichnung]"
                disabled={!isEditMode || useDefaultRecrusTemplate || useDefaultCvTitle}
                label="Custom Email Subject"
                helperText="Customize the email subject line for CV requests."
                sx={{ mb: 2 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1.5,
                  p: 1.5,
                  border: '1px dashed',
                  borderColor: 'primary.light',
                  borderRadius: 1,
                  bgcolor: 'primary.lighter',
                }}
              >
                <CodeIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
                  Available variables:
                </Typography>
                <Chip size="small" label="{{name}}" color="primary" variant="outlined" />
                <Chip size="small" label="{{jobTitle}}" color="primary" variant="outlined" />
                <Chip size="small" label="{{companyName}}" color="primary" variant="outlined" />
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  Paste these into your text — they will be replaced with the candidate&apos;s name,
                  job title, and company name.
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={4}
                value={emailSettings.askForCvCustomText || ''}
                onChange={(e) => handleCvCustomTextChange(e.target.value)}
                placeholder={intl.formatMessage({ id: 'ask-for-cv-placeholder' })}
                disabled={!isEditMode || useDefaultRecrusTemplate}
                helperText={
                  emailSettings.askForCvType === 'upload'
                    ? intl.formatMessage({ id: 'cv-upload-help-text' })
                    : intl.formatMessage({ id: 'cv-custom-help-text' })
                }
              />

              {emailSettings.askForCvType === 'upload' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <FormattedMessage id="cv-upload-preview" />:
                  </Typography>
                  <Button variant="contained" size="small" disabled sx={{ textTransform: 'none' }}>
                    CV hochladen
                  </Button>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 1, color: 'text.secondary' }}
                  >
                    <FormattedMessage id="cv-upload-button-info" />
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setCvPreviewModalOpen(true)}
                  disabled={!emailSettings.askForCvCustomText && !useDefaultRecrusTemplate}
                >
                  Preview CV Request Email
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* CV Follow-up - Expandable - Hidden for Resellers, like the CV request */}
        {!isReseller && (
          <CvFollowUpSettings
            customerId={customerId}
            values={{
              autoFollowUpEnabled: emailSettings.autoFollowUpEnabled,
              followUpDelayDays: emailSettings.followUpDelayDays,
              followUpCustomTitle: emailSettings.followUpCustomTitle,
              followUpCustomText: emailSettings.followUpCustomText,
            }}
            onChange={handleFollowUpChange}
            isEditMode={isEditMode}
            cvRequestWithoutUploadButton={
              emailSettings.askForCvType === 'custom' && !!emailSettings.askForCvCustomText?.trim()
            }
            autoAskForCv={!!emailSettings.autoAskForCvOnIncomingLeads}
          />
        )}

        {/* Preview Button */}
        {emailSettings.emailTemplateBase && (
          <Button
            variant="outlined"
            onClick={() => setPreviewOpen(true)}
            sx={{ mb: 2 }}
            disabled={!hasBodyPlaceholder}
          >
            <FormattedMessage id="preview-template" />
          </Button>
        )}

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <FormattedMessage id="email-template-preview" />
          </DialogTitle>
          <DialogContent>
            <Tabs
              value={previewTab}
              onChange={(_, newValue) => setPreviewTab(newValue)}
              sx={{ mb: 2 }}
            >
              <Tab label="Sample Content" />
              <Tab label="Rejection Text" disabled={!emailSettings.rejectionText} />
              <Tab label="Ask for CV" disabled={!emailSettings.askForCvCustomText} />
            </Tabs>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: getPreviewContent(previewTab === 1, previewTab === 2),
                }}
              />
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>
              <FormattedMessage id="close" />
            </Button>
          </DialogActions>
        </Dialog>

        {/* CV Request Email Preview Dialog */}
        <Dialog
          open={cvPreviewModalOpen}
          onClose={() => setCvPreviewModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">CV Request Email Preview</Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a preview of how the CV request email will look when sent to applicants.
              Placeholders are filled with sample data.
            </Alert>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>To:</strong> applicant@example.com
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Subject:</strong>{' '}
                {emailSettings.askForCvCustomTitle &&
                emailSettings.askForCvCustomTitle.trim() !== ''
                  ? emailSettings.askForCvCustomTitle
                      .replace(/\[Kunde\]/g, customerData?.data?.customer?.name || 'Kundenname')
                      .replace(/\[Stellenbezeichnung\]/g, 'Senior Developer')
                  : `Ihre Bewerbung für ${
                      customerData?.data?.customer?.name || 'Kundenname'
                    }: Senior Developer`}
              </Typography>
            </Box>
            <Box
              sx={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                minHeight: '400px',
                overflow: 'auto',
                bgcolor: 'white',
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
              }}
            >
              <iframe
                srcDoc={getCvPreviewContent()}
                style={{
                  width: '100%',
                  minHeight: '500px',
                  border: 'none',
                }}
                title="CV Email Preview"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCvPreviewModalOpen(false)} color="primary">
              <FormattedMessage id="close" />
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  )
}

export default EmailSettings
